/**
 * BroadcastingInfoRemapper
 *
 * Maps BroadcastingInfoDto (data layer) → BroadcastingInfoEntity (domain).
 * Flutter source: lib/data/models/broadcasting_info.dart
 */
import type { BroadcastingInfoDto } from '@/data/dtos/info/broadcastingInfoDto';
import type { JsonObject } from '@/data/types/json';
import type { BroadcastingInfoEntity } from '@/domain/entities/info/broadcastingInfoEntity';

export class BroadcastingInfoRemapper {
  static fromDto(dto: BroadcastingInfoDto): BroadcastingInfoEntity {
    return { ...dto };
  }

  static fromJson(data: JsonObject): BroadcastingInfoDto {
    return {
      liveStreamingMessageId: (data['liveStreamingMessageId'] as string | undefined) ?? '',
      // Flutter default: -1 (BroadcastingInfo({this.broadcasterStreamingId = -1}))
      broadcasterStreamingId: (data['broadcasterStreamingId'] as number | undefined) ?? -1,
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
      isLive: data['isLive'] as boolean | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): BroadcastingInfoDto[] {
    return dataList.map(BroadcastingInfoRemapper.fromJson);
  }

  /**
   * Serialize a `BroadcastingInfoDto` to the Firestore wire map. Parity with
   * Flutter's `_$BroadcastingInfoToJson` (lib/data/models/broadcasting_info.g.dart:17-23):
   * all four keys are always emitted. `broadcasterStreamingId` defaults to `-1`
   * when unset — mirroring Flutter's constructor default
   * (`BroadcastingInfo({this.broadcasterStreamingId = -1})`), the shape of the
   * end-live notification (community_screen.dart:672-676) which never sets it.
   */
  static toJson(dto: BroadcastingInfoDto): JsonObject {
    return {
      liveStreamingMessageId: dto.liveStreamingMessageId,
      broadcasterStreamingId: dto.broadcasterStreamingId ?? -1,
      timestamp: dto.timestamp,
      isLive: dto.isLive ?? null,
    };
  }

  /** Map a `BroadcastingInfoEntity` back to its DTO (shapes are identical). */
  static toDtoFromEntity(entity: BroadcastingInfoEntity): BroadcastingInfoDto {
    return { ...entity };
  }
}
